import { Icons } from "assets";
import classNames from "classnames";
import { useEffect, useState } from "react";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import CustomToolTip from "shared/components/common/customToolTip";
import { toastMessage } from "shared/components/common/toast";
import SkeletonLoader from "shared/loaders/skeleton";
import ConfirmationModal from "shared/modal/confirmation";
import styles from "./style.module.scss";
import {
  DeleteTestimonialService,
  GetTestimonialsService,
} from "shared/services/testimonials";

interface TestimonialsData {
  id: number;
  name: string;
  text: string;
  type: string;
  user_type: string;
  image: Image;
}
interface Image {
  fullpath: string;
}

interface AdvertisementListingsProps {
  handleUpdate: void | any;
}

const TestimonialListings = ({ handleUpdate }: AdvertisementListingsProps) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedScreen, setSelectedScreen] = useState<string>("homepage");
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    action: string | null;
    ad: TestimonialsData | null;
  }>({
    open: false,
    action: null,
    ad: null,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [deleteLoading, setDeleteLoading] = useState<number | null>(null);
  const [testimonials, setTestimonials] = useState<TestimonialsData[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const openConfirmModal = (action: string, ad: TestimonialsData) => {
    if (action === "Update" && handleUpdate) {
      handleUpdate(ad);
    } else {
      setConfirmModal({ open: true, action, ad });
    }
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, action: null, ad: null });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleScreenChange = (screen: string) => {
    setSelectedScreen(screen);
    setCurrentPage(1);
  };

  const tableHeads = [
    { title: "User Image", key: "image" },
    { title: "User Name", key: "user" },
    { title: "Text", key: "text" },
    { title: "Testimonial Type", key: "type" },
    { title: "User Type", key: "userType" },
    { title: "Action", key: "action" },
  ];

  const handleGetAllTestimonials = async (page: number) => {
    try {
      setLoading(true);
      const response = await GetTestimonialsService();
      setTestimonials(response.data.data.data || []);
      setTotalItems(response.data.data.totalItems || 0);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
      toastMessage("error", "Failed to fetch testimonials.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetAllTestimonials(currentPage);
  }, [selectedScreen, currentPage]);

  const handleDeleteAdvertisement = async (adId: number) => {
    setDeleteLoading(adId);
    try {
      await DeleteTestimonialService(adId);
      toastMessage("success", "Testimonial deleted successfully!");
      setTestimonials((previmages) =>
        previmages.filter((ad) => ad.id !== adId)
      );
    } catch (error) {
      console.error("Error deleting Testimonial:", error);
      toastMessage("error", "Failed to delete Testimonial. Please try again.");
    } finally {
      setDeleteLoading(null);
      closeConfirmModal();
    }
  };

  return (
    <div className={styles.sectionContainer}>
      <div className={classNames(styles.tableWrapper, "relative")}>
        <CustomTable
          heads={tableHeads}
          title="testimonials List"
          isEmpty={!loading && !testimonials.length}
        >
          {loading
            ? Array.from({ length: 2 }).map((_, index) => (
                <TableRowLoader key={index} />
              ))
            : testimonials.map((data) => (
                <tr key={data.id}>
                  <td>
                    {data.image?.fullpath ? (
                      <img
                        src={data.image.fullpath}
                        alt="ads-img"
                        width="100"
                      />
                    ) : (
                      <p>No Image Available</p>
                    )}
                  </td>
                  <td>{data.name}</td>
                  <td>{data.text}</td>
                  <td>{data.type.replace(/^\w/, (c) => c.toUpperCase())}</td>
                  <td>
                    {data.user_type.replace(/^\w/, (c) => c.toUpperCase())}{" "}
                  </td>
                  <td>
                    <div className={classNames(styles.IconsContainer)}>
                      <CustomToolTip label="Update">
                        <button
                          onClick={() => openConfirmModal("Update", data)}
                        >
                          <Icons.UpdateBlog />
                        </button>
                      </CustomToolTip>
                      <CustomToolTip label="Delete">
                        <button
                          onClick={() => openConfirmModal("delete", data)}
                          disabled={deleteLoading === data.id}
                        >
                          {deleteLoading === data.id ? (
                            <SkeletonLoader />
                          ) : (
                            <Icons.Cross />
                          )}
                        </button>
                      </CustomToolTip>
                    </div>
                  </td>
                </tr>
              ))}
        </CustomTable>
      </div>

      <CustomPagination
        onPageChange={handlePageChange}
        totalCount={totalItems}
        currentPage={currentPage}
        pageSize={10}
      />
      {confirmModal.open && (
        <ConfirmationModal
          show={confirmModal.open}
          title="Confirm Delete"
          actionButtonText="Yes, Delete"
          subtitle="Are you sure you want to delete this ad?"
          loading={deleteLoading === confirmModal.ad?.id}
          handleSubmit={() =>
            confirmModal.ad && handleDeleteAdvertisement(confirmModal.ad.id)
          }
          handleClose={closeConfirmModal}
        />
      )}
    </div>
  );
};

const TableRowLoader = () => (
  <tr>
    {Array.from({ length: 6 }).map((_, index) => (
      <td
        key={index}
        className={classNames(styles.tableContentLabel, "px-2 py-3")}
      >
        <div className="flex items-center justify-center">
          <SkeletonLoader className={styles.lineLoader} />
        </div>
      </td>
    ))}
  </tr>
);

export default TestimonialListings;
